// frontend/src/pages/CardCustomizer.jsx
import { useCallback, useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../utils/AuthContext';
import { api } from '../utils/api';
import { supabase } from '../utils/supabase';
import { useLanguage } from '../utils/i18n';
import { useNotification } from '../utils/NotificationContext';
import WalletPassPreview from '../components/personalizer/WalletPassPreview';
import GoogleWalletPreview from '../components/personalizer/GoogleWalletPreview';
import Logo from '../components/Logo';
import LanguageToggle from '../components/LanguageToggle';
import {
    LayoutDashboard,
    Users,
    Palette,
    Settings,
    ChevronLeft,
    Sparkles,
    BarChart3
} from 'lucide-react';

const STAMP_ICONS = ['⭐', '✂️', '💈', '☕', '🍕', '🍔', '💅', '🧁', '🎯', '❤️', '🔥', '💎', '🌟', '🎁', '✅', '👑'];

const PRESET_COLORS = [
    { bg: '#3b82f6', text: '#ffffff', label: 'Primary Blue' },
    { bg: '#0b111c', text: '#f8fafc', label: 'Dark Navy' },
    { bg: '#171c26', text: '#f8fafc', label: 'Slate' },
    { bg: '#7c3aed', text: '#ffffff', label: 'Purple' },
    { bg: '#10b981', text: '#ffffff', label: 'Emerald' },
    { bg: '#ef4444', text: '#ffffff', label: 'Red' },
    { bg: '#f97316', text: '#ffffff', label: 'Orange' },
    { bg: '#f59e0b', text: '#1a1a2e', label: 'Amber' },
    { bg: '#ec4899', text: '#ffffff', label: 'Pink' },
    { bg: '#06b6d4', text: '#ffffff', label: 'Cyber' },
];

export default function CardCustomizer() {
    const { business, refetchBusiness, isDemo } = useAuth();
    const { t } = useLanguage();
    const { showNotification } = useNotification();
    const location = useLocation();

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [logoUploading, setLogoUploading] = useState(false);
    const [logoError, setLogoError] = useState(null);
    const [previewCount, setPreviewCount] = useState(3);
    const [previewMode, setPreviewMode] = useState('apple');

    const [bgColor, setBgColor] = useState('#1a73e8');
    const [textColor, setTextColor] = useState('#ffffff');
    const [stampIcon, setStampIcon] = useState('⭐');
    const [stampGoal, setStampGoal] = useState(10);
    const [rewardText, setRewardText] = useState('Free reward!');
    const [cardTitle, setCardTitle] = useState('');
    const [logoUrl, setLogoUrl] = useState('');
    const [stampColor, setStampColor] = useState('rgba(255,255,255,0.25)');
    const [numberColor, setNumberColor] = useState('rgba(255,255,255,0.3)');
    const [headerMode, setHeaderMode] = useState(isDemo ? 'logo' : 'title');

    const fetchDesign = useCallback(async () => {
        if (!business?.id) return;
        try {
            const data = await api.getDesign(business.id);
            if (data) {
                setBgColor(data.bg_color || '#1a73e8');
                setTextColor(data.text_color || '#ffffff');
                setStampIcon(data.stamp_icon || '⭐');
                setStampGoal(data.stamp_goal || 10);
                setRewardText(data.reward_text || 'Free reward!');
                setCardTitle(data.card_title || business.name || '');
                setLogoUrl(data.logo_url || '');
                setStampColor(data.stamp_color || 'rgba(255,255,255,0.25)');
                setNumberColor(data.number_color || 'rgba(255,255,255,0.3)');
                setCardTitle(business.name || 'Loyalty Card');
                if (isDemo) setHeaderMode('logo');
            }
        } catch (err) {
            console.warn('Failed to load design:', err.message);
            setCardTitle(business.name || 'Loyalty Card');
        } finally {
            setLoading(false);
        }
    }, [business?.id, business?.name]);

    useEffect(() => { fetchDesign(); }, [fetchDesign]);

    const ALLOWED_TYPES = ['image/png', 'image/jpeg', 'image/webp'];
    const MAX_SIZE = 2 * 1024 * 1024; // 2MB

    // Helper to delete an orphaned logo from Supabase Storage
    const deleteOldLogo = async (oldUrl) => {
        if (!oldUrl || oldUrl.startsWith('blob:')) return;
        try {
            // URL format: https://[project].supabase.co/storage/v1/object/public/logos/[business_id]/[filename]
            const parts = oldUrl.split('/logos/');
            if (parts.length === 2) {
                const storagePath = parts[1]; // e.g: "b2df.../1715...-logo.png"
                await supabase.storage.from('logos').remove([storagePath]);
                console.log(`[Storage Cleanup] Deleted old logo: ${storagePath}`);
            }
        } catch (err) {
            console.warn('[Storage Cleanup] Failed to delete old logo:', err.message);
        }
    };

    async function handleLogoUpload(file) {
        setLogoError(null);

        if (!ALLOWED_TYPES.includes(file.type)) {
            setLogoError(t('err_logo_wrong_type'));
            return;
        }
        if (file.size > MAX_SIZE) {
            setLogoError(t('err_logo_too_large'));
            return;
        }

        // Instant local preview while upload happens in background
        const previousUrl = logoUrl;
        const localPreview = URL.createObjectURL(file);
        setLogoUrl(localPreview);

        if (isDemo) {
            showNotification(t('demo_mode_preview_only') || 'Demo mode: Logo only for preview', 'info');
            return;
        }

        setLogoUploading(true);
        try {
            const path = `${business.id}/${Date.now()}-${file.name}`;
            const { error: uploadError } = await supabase.storage
                .from('logos')
                .upload(path, file, { upsert: true });

            if (uploadError) throw uploadError;

            const { data: urlData } = supabase.storage
                .from('logos')
                .getPublicUrl(path);

            const publicUrl = urlData.publicUrl;

            // Persist to businesses table
            await supabase
                .from('businesses')
                .update({ logo_url: publicUrl })
                .eq('id', business.id);

            // Replace local blob with the real Supabase URL
            URL.revokeObjectURL(localPreview);
            setLogoUrl(publicUrl);

            // Clean up old logo from storage
            if (previousUrl && !previousUrl.startsWith('blob:')) {
                await deleteOldLogo(previousUrl);
            }
        } catch (err) {
            console.error('[Logo Upload]', err);
            URL.revokeObjectURL(localPreview);
            setLogoUrl(previousUrl);
            setLogoError(t('err_logo_upload_failed'));
        } finally {
            setLogoUploading(false);
        }
    }

    async function handleRemoveLogo() {
        if (isDemo) {
            setLogoUrl(null);
            return;
        }
        setLogoUploading(true);
        setLogoError(null);
        try {
            await supabase
                .from('businesses')
                .update({ logo_url: null })
                .eq('id', business.id);

            const oldUrl = logoUrl;
            setLogoUrl(null);

            if (oldUrl && !oldUrl.startsWith('blob:')) {
                await deleteOldLogo(oldUrl);
            }
        } catch (err) {
            console.error('[Remove Logo]', err);
            setLogoError(t('err_logo_upload_failed'));
        } finally {
            setLogoUploading(false);
        }
    }

    async function handleSave() {
        if (!business?.id) return;

        if (isDemo) {
            showNotification(t('demo_save_simulated') || 'Design preview updated successfully!', 'success');
            return;
        }

        setSaving(true);
        try {
            await api.saveDesign(business.id, {
                bg_color: bgColor,
                text_color: textColor,
                stamp_icon: stampIcon,
                stamp_goal: stampGoal,
                reward_text: rewardText,
                card_title: cardTitle,
                logo_url: logoUrl,
                stamp_color: stampColor,
                number_color: numberColor,
                header_mode: headerMode
            });
            showNotification(t('design_saved_notify'), 'success');
            refetchBusiness();
        } catch (err) {
            showNotification(err.message, 'error');
        } finally {
            setSaving(false);
        }
    }

    function applyPreset(preset) {
        setBgColor(preset.bg);
        setTextColor(preset.text);
    }

    if (!business) return (
        <div className="page container" style={{ textAlign: 'center', paddingTop: 80 }}>
            <p>{t('loading')}</p>
        </div>
    );

    return (
        <div className="premium-dashboard">
            {/* 1. Fixed Top Header */}
            <header className="fixed-header">
                <Link to="/dashboard" className="nav-brand-group" style={{ textDecoration: 'none' }}>
                    <ChevronLeft size={24} color="var(--text-muted)" />
                    <span className="brand-text" style={{ fontSize: 18, marginLeft: 4 }}>{t('design')}</span>
                </Link>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <div className="header-biz-name">{business.name}</div>
                    <LanguageToggle />
                </div>
            </header>

            <div className="dashboard-content-scroll">
                <div className="container">
                    {loading ? (
                        <div style={{ textAlign: 'center', paddingTop: 60, color: 'var(--text-muted)' }}>
                            {t('loading')}
                        </div>
                    ) : (
                        <div className="card-customizer-grid">
                            {/* Card Preview - Comes first on mobile */}
                            <div className="preview-column">
                                <h3 className="preview-title">{t('preview')}</h3>

                                <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 20 }}>
                                    <div style={{ background: 'var(--surface)', borderRadius: 12, padding: 4, display: 'flex', gap: 4, border: '1px solid var(--border)' }}>
                                        <button onClick={() => setPreviewMode('apple')} style={{ padding: '8px 16px', borderRadius: 10, border: 'none', background: previewMode === 'apple' ? 'var(--bg)' : 'transparent', color: previewMode === 'apple' ? 'var(--text)' : 'var(--text-muted)', fontWeight: 600, fontSize: 13, cursor: 'pointer' }}>{t('apple')}</button>
                                        <button onClick={() => setPreviewMode('google')} style={{ padding: '8px 16px', borderRadius: 10, border: 'none', background: previewMode === 'google' ? 'var(--bg)' : 'transparent', color: previewMode === 'google' ? 'var(--text)' : 'var(--text-muted)', fontWeight: 600, fontSize: 13, cursor: 'pointer' }}>{t('google')}</button>
                                    </div>
                                </div>

                                <div className="card-preview-wrapper">
                                    {previewMode === 'apple' ? (
                                        <WalletPassPreview
                                            bgColor={bgColor}
                                            textColor={textColor}
                                            stampIcon={stampIcon}
                                            stampGoal={stampGoal}
                                            currentCount={previewCount}
                                            rewardText={rewardText}
                                            cardTitle={cardTitle || business.name}
                                            logoUrl={logoUrl}
                                            clientName="John Doe"
                                            stampColor={stampColor}
                                            numberColor={numberColor}
                                            headerMode={headerMode}
                                        />
                                    ) : (
                                        <GoogleWalletPreview
                                            bgColor={bgColor}
                                            textColor={textColor}
                                            stampIcon={stampIcon}
                                            stampGoal={stampGoal}
                                            currentCount={previewCount}
                                            rewardText={rewardText}
                                            cardTitle={cardTitle || business.name}
                                            logoUrl={logoUrl}
                                            clientName="John Doe"
                                            stampColor={stampColor}
                                            numberColor={numberColor}
                                            headerMode={headerMode}
                                        />
                                    )}
                                </div>

                            </div>

                            {/* Design Controls */}
                            <div className="design-controls-card">
                                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 24 }}>
                                    <Sparkles size={20} color="var(--primary)" />
                                    <h2 style={{ fontSize: 20, fontWeight: 700, color: 'var(--text)' }}>
                                        {t('customize_card')}
                                    </h2>
                                </div>

                                <div className="form-group">
                                    <label>{t('card_title')}</label>
                                    <input
                                        value={cardTitle}
                                        onChange={e => setCardTitle(e.target.value)}
                                        placeholder={business.name}
                                    />
                                </div>

                                <div className="form-group">
                                    <label>{t('wallet_header')}</label>
                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                                        <button
                                            onClick={() => setHeaderMode('title')}
                                            className={`btn btn-sm ${headerMode === 'title' ? 'btn-primary' : 'btn-outline'}`}
                                            style={{ background: headerMode === 'title' ? '' : 'var(--bg)', borderColor: headerMode === 'title' ? '' : 'var(--border)' }}
                                        >
                                            {t('name')}
                                        </button>
                                        <button
                                            onClick={() => setHeaderMode('logo')}
                                            className={`btn btn-sm ${headerMode === 'logo' ? 'btn-primary' : 'btn-outline'}`}
                                            style={{ background: headerMode === 'logo' ? '' : 'var(--bg)', borderColor: headerMode === 'logo' ? '' : 'var(--border)' }}
                                        >
                                            {t('logo')}
                                        </button>
                                    </div>
                                </div>

                                <div className="form-group">
                                    <label>{t('logo_upload')}</label>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                        {/* Preview / Spinner area */}
                                        <div style={{
                                            width: 48, height: 48, borderRadius: 10,
                                            border: '1px solid var(--border)',
                                            background: 'var(--bg)',
                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            overflow: 'hidden', flexShrink: 0
                                        }}>
                                            {logoUploading ? (
                                                <div className="logo-spinner" />
                                            ) : logoUrl ? (
                                                <img src={logoUrl} alt="Logo" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                            ) : (
                                                <span style={{ fontSize: 20, color: 'var(--text-muted)' }}>📷</span>
                                            )}
                                        </div>

                                        {/* Upload / Change button */}
                                        <div style={{ flex: 1 }}>
                                            {logoUrl && !logoUploading ? (
                                                <div style={{ display: 'flex', gap: 8 }}>
                                                    <button
                                                        type="button"
                                                        className="btn btn-sm btn-outline"
                                                        style={{ background: 'var(--bg)', borderColor: 'var(--border)' }}
                                                        onClick={() => document.getElementById('logo-file-input').click()}
                                                    >
                                                        {t('logo_change')}
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className="btn btn-sm btn-outline"
                                                        style={{ background: 'var(--bg)', borderColor: 'var(--border)', color: '#ef4444' }}
                                                        onClick={handleRemoveLogo}
                                                    >
                                                        {t('logo_remove')}
                                                    </button>
                                                </div>
                                            ) : !logoUploading ? (
                                                <button
                                                    type="button"
                                                    className="btn btn-sm btn-outline"
                                                    style={{ background: 'var(--bg)', borderColor: 'var(--border)' }}
                                                    onClick={() => document.getElementById('logo-file-input').click()}
                                                >
                                                    {t('logo_upload_hint')}
                                                </button>
                                            ) : null}
                                            <input
                                                id="logo-file-input"
                                                type="file"
                                                accept="image/png, image/jpeg, image/webp"
                                                style={{ display: 'none' }}
                                                onChange={e => {
                                                    const file = e.target.files?.[0];
                                                    if (file) handleLogoUpload(file);
                                                    e.target.value = '';
                                                }}
                                            />
                                        </div>
                                    </div>
                                    {logoError && (
                                        <p style={{ color: '#ef4444', fontSize: 12, marginTop: 6, marginBottom: 0 }}>{logoError}</p>
                                    )}
                                </div>

                                <div className="form-group">
                                    <label>{t('color_preset')}</label>
                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                                        {PRESET_COLORS.map((preset, i) => (
                                            <button
                                                key={i}
                                                onClick={() => applyPreset(preset)}
                                                style={{
                                                    width: 32,
                                                    height: 32,
                                                    borderRadius: 8,
                                                    border: bgColor === preset.bg ? '2px solid #fff' : '1px solid var(--border-bright)',
                                                    background: preset.bg,
                                                    cursor: 'pointer'
                                                }}
                                            />
                                        ))}
                                    </div>
                                </div>

                                <div className="colors-grid">
                                    <div className="form-group">
                                        <label>{t('background_color')}</label>
                                        <div style={{ display: 'flex', gap: 8 }}>
                                            <input type="color" value={bgColor} onChange={e => setBgColor(e.target.value)} style={{ width: 44, padding: 2, height: 40 }} />
                                            <input value={bgColor} onChange={e => setBgColor(e.target.value)} style={{ flex: 1, fontSize: 13 }} />
                                        </div>
                                    </div>
                                    <div className="form-group">
                                        <label>{t('text_color')}</label>
                                        <div style={{ display: 'flex', gap: 8 }}>
                                            <input type="color" value={textColor} onChange={e => setTextColor(e.target.value)} style={{ width: 44, padding: 2, height: 40 }} />
                                            <input value={textColor} onChange={e => setTextColor(e.target.value)} style={{ flex: 1, fontSize: 13 }} />
                                        </div>
                                    </div>
                                    <div className="form-group">
                                        <label>{t('stamp_circle_color') || 'Stamp Color'}</label>
                                        <div style={{ display: 'flex', gap: 8 }}>
                                            <input type="color" value={stampColor.startsWith('rgba') ? '#ffffff' : stampColor} onChange={e => setStampColor(e.target.value)} style={{ width: 44, padding: 2, height: 40 }} />
                                            <input value={stampColor} onChange={e => setStampColor(e.target.value)} style={{ flex: 1, fontSize: 13 }} />
                                        </div>
                                    </div>
                                    <div className="form-group">
                                        <label>{t('number_color') || 'Number Color'}</label>
                                        <div style={{ display: 'flex', gap: 8 }}>
                                            <input type="color" value={numberColor.startsWith('rgba') ? '#ffffff' : numberColor} onChange={e => setNumberColor(e.target.value)} style={{ width: 44, padding: 2, height: 40 }} />
                                            <input value={numberColor} onChange={e => setNumberColor(e.target.value)} style={{ flex: 1, fontSize: 13 }} />
                                        </div>
                                    </div>
                                </div>

                                <div className="form-group">
                                    <label>{t('stamp_icon')}</label>
                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                                        {STAMP_ICONS.map(icon => (
                                            <button
                                                key={icon}
                                                onClick={() => setStampIcon(icon)}
                                                style={{
                                                    width: 40,
                                                    height: 40,
                                                    borderRadius: 10,
                                                    border: stampIcon === icon ? '2px solid var(--primary)' : '1px solid var(--border)',
                                                    background: stampIcon === icon ? 'rgba(59,130,246,0.1)' : 'var(--bg)',
                                                    fontSize: 20,
                                                    cursor: 'pointer'
                                                }}
                                            >
                                                {icon}
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                <div className="inputs-grid">
                                    <div className="form-group">
                                        <label>{t('stamp_goal')}</label>
                                        <input
                                            type="number"
                                            min={3}
                                            max={10}
                                            value={stampGoal}
                                            onChange={e => setStampGoal(Math.max(3, Math.min(10, parseInt(e.target.value) || 10)))}
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label>{t('preview_stamps')}</label>
                                        <input
                                            type="range"
                                            min={0}
                                            max={stampGoal}
                                            value={previewCount}
                                            onChange={e => setPreviewCount(parseInt(e.target.value))}
                                            style={{ accentColor: 'var(--primary)', height: 36 }}
                                        />
                                    </div>
                                </div>

                                <div className="form-group" style={{ marginBottom: 32 }}>
                                    <label>{t('reward_text')}</label>
                                    <input value={rewardText} onChange={e => setRewardText(e.target.value)} />
                                </div>

                                <button
                                    className="btn-primary-gradient"
                                    onClick={handleSave}
                                    disabled={saving}
                                    style={{ height: 56, fontSize: 16 }}
                                >
                                    {saving ? t('saving') : t('save_design')}
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* 7. Bottom Navigation Bar */}
            <nav className="bottom-nav-premium">
                <Link to="/dashboard" className="sidebar-brand-premium">
                    <Logo size={28} />
                    <span className="sidebar-brand-text">Wafiyo</span>
                </Link>
                <Link to="/dashboard" state={{ tab: 'overview' }} className="nav-tab-premium">
                    <LayoutDashboard size={24} />
                    <span>{t('dashboard')}</span>
                </Link>
                <Link to="/dashboard" state={{ tab: 'clients' }} className="nav-tab-premium">
                    <Users size={24} />
                    <span>{t('clients')}</span>
                </Link>
                <Link to="/dashboard/card-design" className={`nav-tab-premium ${location.pathname === '/dashboard/card-design' ? 'active' : ''}`}>
                    <Palette size={24} />
                    <span>{t('design')}</span>
                </Link>
                <Link to="/dashboard" state={{ tab: 'settings' }} className="nav-tab-premium">
                    <Settings size={24} />
                    <span>{t('settings')}</span>
                </Link>
            </nav>


        </div>

    );
}
